import {
  defaultSubEventTicket,
  registrationBasedSubEventSchema,
  type EventDatesDataType,
  type SubEventFormDataType,
} from '@/schemas/subEventForm.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { DollarIcon } from '../Icons'
import CustomRichTextEditor from '../CustomRichTextEditor'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbRouterLink,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import {
  EVENT_TABS,
  extractSponsorShipPlan,
  getFreeTicket,
  getSubEventTickets,
  registrationOptions,
  transformSponsorShipPlan,
} from '@/constants/event'
import { ErrorMessage } from '../ErrorMessage'
import {
  handleIntegerKeyDown,
  handlePriceInput,
  handlePriceKeyDown,
  handlePriceClick,
  formatPriceWithCommas,
} from '@/utils/formFieldUtils'
import { Textarea } from '../ui/textarea'
import { UnsavedChangesDialog } from '../UnsavedChangesDialog'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { Button } from '../ui/button'
import { useNavigate, useParams } from 'react-router-dom'
import { PATHROUTES } from '@/constants/path'
import { showErrorToast, showSuccessToast } from '../ui/toast'
import { Info } from 'lucide-react'
import {
  createSubEvent,
  type createSubEventPayload,
  type EventSponsorShipPayloadType,
} from '@/api/subEventApi'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { handleAxiosError } from '@/utils/helper'
import { getSubEventById, updateSubEvent } from '@/api/subEventApi'
import { SUB_EVENT_QUERY_KEYS } from '@/api/queryKeys'
import { useEffect, useRef } from 'react'
import type { SubEventData } from '@/schemas/subEvent.schema'
import CustomLoader from '../CustomLoader'
import { defaultSponsorshipPackages } from '@/schemas/eventForm.schema'
import { getEventDates } from '@/api/eventApi'
import EventPackageForm, {
  type EventPackageFormRef,
} from '../event/EventPackageForm'

export interface SubEventTicket {
  id?: string
  name: string
  price: number | undefined
  description: string
  perks: string
  maxAttendeesLimit: number
}

export default function AddEditSubEvent() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { mainEventId } = useParams<{ mainEventId: string }>()
  const eventPackageFormRef = useRef<EventPackageFormRef>(null)

  const isEditMode = !!id

  // Fetch sub-event data for edit mode
  const { data: subEventData, isLoading: isLoadingSubEvent } = useQuery({
    queryKey: SUB_EVENT_QUERY_KEYS.byId(id as string),
    queryFn: () => getSubEventById(id as string),
    enabled: isEditMode,
  })

  const finalMainEventId = mainEventId || subEventData?.data?.data?.event?.id
  const { data: mainEventDates } = useQuery({
    queryKey: SUB_EVENT_QUERY_KEYS.mainEventDates(finalMainEventId as string),
    queryFn: () => getEventDates(finalMainEventId as string),
    enabled: !!finalMainEventId,
  })

  const mainEventDatesData: EventDatesDataType = mainEventDates?.data

  const form = useForm<SubEventFormDataType>({
    resolver: zodResolver(registrationBasedSubEventSchema),
    defaultValues: {
      eventName: '',
      eventDescription: '',
      registrationType: 'free',
      eventTicket: defaultSubEventTicket,
      maxAttendees: '',
      sponsorshipPackages: defaultSponsorshipPackages,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  })

  const { handleSubmit, getValues, reset } = form

  useEffect(() => {
    if (isEditMode && subEventData?.data) {
      const data: SubEventData = subEventData?.data?.data

      const packages = extractSponsorShipPlan(data.sponsorshipPlans)

      const formData: SubEventFormDataType = {
        eventName: data.name,
        eventDescription: data.description || '',
        registrationType: data.type === 'free' ? 'free' : 'paid',
        eventTicket: getSubEventTickets(data.type, data.tickets),
        maxAttendees: data.tickets?.[0]?.maxAttendeesLimit?.toString() || '',
        ...getFreeTicket(data.type, data.tickets?.[0]),
        sponsorshipPackages: packages,
      }

      reset(formData)
    }
  }, [subEventData])

  const registrationType = getValues('registrationType')
  const eventName = getValues('eventName')
  const maxAttendees = getValues('maxAttendees')

  const hasFormChanged = form.formState.isDirty

  // Disable submit button conditions
  const isSubmitDisabled =
    !eventName || !maxAttendees || (isEditMode && !hasFormChanged)

  const { showDialog, handleConfirm, handleCancel, allowNavigation } =
    useUnsavedChanges(form)

  const handleCancelClick = () => {
    allowNavigation()
    navigate(
      `${PATHROUTES.DASHBOARD.EVENT_MANAGEMENT}?tab=${EVENT_TABS.SUB_EVENT}`
    )
  }

  const transformFormDataToPayload = (
    data: SubEventFormDataType
  ): Promise<createSubEventPayload> => {
    const subEvent: SubEventData = subEventData?.data?.data

    const eventTicketData: SubEventTicket[] = []
    if (data.registrationType === 'paid' && data.eventTicket) {
      const newTicket: SubEventTicket = {
        name: data.eventTicket.name || '',
        price: data.eventTicket.price
          ? Number(data.eventTicket.price)
          : undefined,
        description: data.eventTicket.description || '',
        perks: data.eventTicket.perks || '',
        maxAttendeesLimit: Number(data.maxAttendees),
      }

      if (id && !data.eventTicket.isNew) {
        newTicket.id = data.eventTicket.id
      }

      eventTicketData.push(newTicket)
    } else if (data.registrationType === 'free') {
      const newTicket: SubEventTicket = {
        name: data.ticketName || '',
        price: 0,
        description: data.ticketDescription || '',
        perks: data.ticketPerks || '',
        maxAttendeesLimit: Number(data.maxAttendees),
      }

      if (id && subEvent?.type === registrationOptions[0].id) {
        newTicket.id = subEvent?.tickets[0]?.id
      }

      eventTicketData.push(newTicket)
    }

    const packagePlans = form.getValues('sponsorshipPackages')

    const sponsorshipPackages: EventSponsorShipPayloadType[] =
      transformSponsorShipPlan(packagePlans)

    return new Promise(resolve => {
      const payload: createSubEventPayload = {
        eventId: mainEventId ?? subEvent?.event?.id,
        name: data.eventName,
        description: data.eventDescription,
        type: data.registrationType,
        maxAttendeesLimit: Number(data.maxAttendees),
        tickets: eventTicketData,
        sponsorshipPackages: sponsorshipPackages,
      }
      resolve(payload)
    })
  }

  const queryClient = useQueryClient()

  const { mutate: createSubEventMutation, isPending: isCreatingSubEvent } =
    useMutation({
      mutationFn: createSubEvent,
      onSuccess: () => {
        allowNavigation()
        const subEventName = getValues('eventName')
        showSuccessToast(
          `Sub Event Created successfully`,
          `${subEventName} has been added to the system and is ready for management.`
        )
        queryClient.invalidateQueries({
          queryKey: SUB_EVENT_QUERY_KEYS.allSubEvents,
        })
        navigate(
          `${PATHROUTES.DASHBOARD.EVENT_MANAGEMENT}?tab=${EVENT_TABS.SUB_EVENT}`
        )
      },
      onError: error => {
        handleAxiosError('Failed to create sub event', error)
      },
    })

  const { mutate: updateSubEventMutation, isPending: isUpdatingSubEvent } =
    useMutation({
      mutationFn: ({
        id,
        payload,
      }: {
        id: string
        payload: createSubEventPayload
      }) => updateSubEvent(id, payload),
      onSuccess: () => {
        allowNavigation()
        const subEventName = getValues('eventName')
        showSuccessToast(
          `Sub Event Updated successfully`,
          `${subEventName} has been updated successfully.`
        )
        queryClient.invalidateQueries({
          queryKey: SUB_EVENT_QUERY_KEYS.allSubEvents,
        })
        queryClient.invalidateQueries({
          queryKey: SUB_EVENT_QUERY_KEYS.byId(id as string),
        })
        navigate(
          `${PATHROUTES.DASHBOARD.EVENT_MANAGEMENT}?tab=${EVENT_TABS.SUB_EVENT}`
        )
      },
      onError: error => {
        handleAxiosError('Failed to update sub event', error)
      },
    })

  const onSubmit = async (data: SubEventFormDataType) => {
    const hasHeroDurationError =
      eventPackageFormRef.current?.hasAnyPackageErrors()

    if (hasHeroDurationError) {
      showErrorToast(
        'Validation Error',
        'Please check and fix the highlighted fields.'
      )
      return
    }
    try {
      const payload = await transformFormDataToPayload(data)
      if (isEditMode && id) {
        updateSubEventMutation({ id, payload })
      } else {
        createSubEventMutation(payload)
      }
    } catch (error) {
      console.error('Event submission error:', error)
      showErrorToast('Event Submission Failed', 'Failed to submit event')
    }
  }

  const onError = (errors: Record<string, unknown>) => {
    console.log('Validation Errors:', errors)
    showErrorToast(
      'Validation Error',
      'Please check and fix the highlighted fields.'
    )
  }

  // Show loading state while fetching sub-event data in edit mode
  if (isEditMode && isLoadingSubEvent) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-30px)]">
        <CustomLoader />
        <span className="mt-40 ml-12">Loading sub event data...</span>
      </div>
    )
  }

  return (
    <div className="@container/main flex flex-1 flex-col gap-2.5 p-4 lg:p-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbRouterLink
              to={`${PATHROUTES.DASHBOARD.EVENT_MANAGEMENT}?tab=${EVENT_TABS.SUB_EVENT}`}
              className="text-ss-neutral-600 hover:text-ss-blue-500 font-medium"
            >
              Event Management
            </BreadcrumbRouterLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-ss-neutral-400" />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-ss-blue-500 font-medium">
              {id ? 'Edit' : 'Create'} Sub Event
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-ss-neutral-950 leading-8">
            {id ? 'Edit' : 'Create'} Sub Event
          </h1>
          <p className="text-sm text-ss-neutral-600 leading-4.5 ">
            {id
              ? 'Update your sub event details, tickets. Changes will be reflected instantly for attendees.'
              : 'Fill in the details below to set up your sub event. You can always edit later.'}
          </p>
        </div>
      </div>
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="space-y-6 border rounded-xl"
        >
          <Card className="border-none rounded-bl-none rounded-br-none bg-ss-neutral-white gap-0 pt-0 mb-0">
            <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3 rounded-tr-xl rounded-tl-xl">
              <Info className="h-5 w-5 text-ss-neutral-600" />
              <CardTitle className="text-lg font-semibold text-ss-neutral-950">
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-1">
                <Label
                  variant="required"
                  htmlFor="eventName"
                  className="text-sm font-semibold text-ss-neutral-950"
                >
                  Event Name
                </Label>
                <Controller
                  name="eventName"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="relative">
                      <Input
                        {...field}
                        id="eventName"
                        type="text"
                        placeholder="e.g., Summer Wellness Retreat"
                        className={cn(
                          'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                          error && 'border-ss-critical-200'
                        )}
                        aria-invalid={!!error}
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label
                  variant="required"
                  htmlFor="eventDescription"
                  className="text-sm font-semibold text-ss-neutral-950"
                >
                  Event Description
                </Label>
                <Controller
                  name="eventDescription"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="relative">
                      <Input
                        {...field}
                        id="eventDescription"
                        type="text"
                        placeholder="e.g., Summer Wellness Retreat"
                        className={cn(
                          'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                          error && 'border-ss-critical-200'
                        )}
                        aria-invalid={!!error}
                      />
                      {error && (
                        <p className="text-sm text-ss-critical-200 mt-1">
                          {error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </CardContent>
            <CardHeader className="pb-4 border-t border-b border-black/6 bg-ss-neutral-25 flex flex-row items-center gap-2 px-4 py-3">
              <DollarIcon className="h-5 w-5 text-ss-neutral-600" />
              <CardTitle className="text-lg font-semibold text-ss-neutral-950 flex gap-1 items-center">
                Pricing & Registration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Registration Type */}
              <section className="space-y-2.5 max-w-[541px]">
                <Label className="text-sm font-semibold text-ss-neutral-950">
                  Registration Type
                </Label>
                <Controller
                  name="registrationType"
                  control={form.control}
                  render={({ field }) => (
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="space-y-3 mb-4 flex gap-4"
                      aria-label="Registration type options"
                    >
                      {registrationOptions.map(option => (
                        <div key={option.id} className="relative mb-0 gap-3">
                          <Label
                            htmlFor={option.id}
                            className={cn(
                              'flex items-center gap-2 rounded-lg cursor-pointer transition-all duration-200',
                              field.value === option.id
                                ? 'border-ss-blue-500 bg-white'
                                : 'border-border bg-background'
                            )}
                          >
                            <RadioGroupItem
                              value={option.id}
                              id={option.id}
                              className={cn(
                                field.value === option.id
                                  ? 'border-ss-blue-500 bg-ss-blue-500 text-white'
                                  : 'border-border bg-background'
                              )}
                              aria-label={option.title}
                            />
                            <span className="font-normal text-ss-neutral-950 text-base leading-[22px]">
                              {option.title}
                            </span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                />
              </section>

              {registrationType === registrationOptions[1].id && (
                <div className="space-y-4">
                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex flex-col flex-1">
                      <Label
                        variant="required"
                        htmlFor="eventTicket.name"
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Ticket Name
                      </Label>
                      <Controller
                        name="eventTicket.name"
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              id="eventTicket.name"
                              type="text"
                              placeholder="Enter ticket name"
                              className={cn(
                                'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                    <div className="flex flex-col flex-1">
                      <Label
                        variant="required"
                        htmlFor="eventTicket.price"
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Ticket Price
                      </Label>
                      <Controller
                        name="eventTicket.price"
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <div className="relative">
                              <Input
                                {...field}
                                value={formatPriceWithCommas(
                                  field.value?.toString() || ''
                                )}
                                onKeyDown={handlePriceKeyDown}
                                onChange={e =>
                                  handlePriceInput(e, field.onChange)
                                }
                                onClick={handlePriceClick}
                                onFocus={handlePriceClick}
                                id="eventTicket.price"
                                type="text"
                                inputMode="decimal"
                                min={0}
                                placeholder="Enter ticket price"
                                className={cn(
                                  'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20 pl-8',
                                  error && 'border-ss-critical-200'
                                )}
                                aria-invalid={!!error}
                              />
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ss-neutral-600 text-base">
                                $
                              </span>
                            </div>
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor="ticket"
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Description
                      </Label>
                      <Controller
                        name="eventTicket.description"
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Textarea
                              {...field}
                              id="eventTicket.description"
                              placeholder="Add ticket description"
                              className={cn(
                                'h-[137px] resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor="eventTicket.perks"
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        What's Included
                      </Label>
                      <Controller
                        name="eventTicket.perks"
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <CustomRichTextEditor
                              toolbarFeatures={['bulletList']}
                              value={field.value ?? ''}
                              onChange={value =>
                                field.onChange(value === '<p></p>' ? '' : value)
                              }
                              height={'85px'}
                              placeholder="Add ticket specification"
                              className={cn(
                                'resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                                error && 'border-ss-critical-200'
                              )}
                              aria-invalid={!!error}
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {registrationType === registrationOptions[0].id && (
                <>
                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex flex-col flex-1">
                      <Label
                        variant="required"
                        htmlFor={`ticketName`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Ticket Name
                      </Label>
                      <Controller
                        name={`ticketName`}
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Input
                              {...field}
                              value={field.value?.toString() || ''}
                              id={`ticketName`}
                              required
                              type="text"
                              placeholder="Enter ticket name"
                              className="h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 w-full sm:flex-row flex-col">
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor={`ticketDescription`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        Description
                      </Label>
                      <Controller
                        name={`ticketDescription`}
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Textarea
                              {...field}
                              value={field.value?.toString() || ''}
                              id={`ticketDescription`}
                              required
                              placeholder="Add ticket description"
                              className="h-[137px] resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                    <div className="flex-1 flex flex-col min-h-px min-w-px">
                      <Label
                        variant="required"
                        htmlFor={`ticketPerks`}
                        className="text-sm font-medium text-ss-neutral-950 leading-4.5  mb-1"
                      >
                        What's Included
                      </Label>
                      <Controller
                        name={`ticketPerks`}
                        control={form.control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <CustomRichTextEditor
                              toolbarFeatures={['bulletList']}
                              value={field.value ?? ''}
                              onChange={value =>
                                field.onChange(value === '<p></p>' ? '' : value)
                              }
                              height={'85px'}
                              placeholder="Add ticket specification"
                              className="resize-none border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 focus:ring-ss-blue-500/20"
                            />
                            {error && <ErrorMessage error={error.message} />}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="max-w-[539px]">
                <Label
                  variant="required"
                  htmlFor="maxAttendees"
                  className="text-sm font-semibold text-ss-neutral-950 leading-4.5  mb-1 block"
                >
                  Max Attendees Limit
                </Label>
                <Controller
                  name="maxAttendees"
                  control={form.control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <Input
                        {...field}
                        value={field.value?.toString() || ''}
                        onChange={e => field.onChange(e.target.value || '')}
                        onKeyDown={handleIntegerKeyDown}
                        id="maxAttendees"
                        type="number"
                        placeholder="Enter max attendees limit"
                        min={1}
                        className={cn(
                          'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                          error && 'border-ss-critical-200'
                        )}
                        aria-invalid={!!error}
                      />
                      {error && <ErrorMessage error={error.message} />}
                    </>
                  )}
                />
              </div>
            </CardContent>

            <div>
              <EventPackageForm
                ref={eventPackageFormRef}
                startDate={mainEventDatesData?.startDateTime}
                endDate={mainEventDatesData?.endDateTime}
                isSubEvent
              />
            </div>

            <div className="bg-white border-t border-ss-neutral-50 rounded-b-xl px-6 py-4 shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)]">
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelClick}
                  // disabled={isCreatingEvent || isUpdatingEvent || isUploading}
                  className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-50 font-semibold"
                >
                  Cancel
                </Button>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    className="h-9 px-4 py-2.5 text-sm font-semibold text-ss-neutral-white bg-ss-blue-900 hover:bg-ss-blue-800 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={
                      isEditMode ? 'Update sub event' : 'Publish sub event'
                    }
                    disabled={
                      isSubmitDisabled ||
                      isCreatingSubEvent ||
                      isUpdatingSubEvent
                    }
                  >
                    {isEditMode ? 'Update Sub Event' : 'Publish Sub Event'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </form>
      </FormProvider>
      <UnsavedChangesDialog
        open={showDialog}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  )
}
